import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Box,
} from "@material-ui/core";
import A_TextField from "../../../../../Atoms/Inputs/TextFields/A_TextField";
import A_Select from "../../../../../Atoms/Inputs/Selects/A_Select";
import A_Button from "../../../../../Atoms/Inputs/Buttons/A_Button";
import * as toastActions from "../../../../../../../store/actions/toastActions";
import { useSelector } from "react-redux";
import useStyles from "./AddPaymentDetails.styles";
import {
  buildBorrowerOptionSetObject,
  ExecuteSqlStoreProc,
  toOptionLabel,
} from "../../Add_Borrower/Borrower_Form_helper";
import { getCRMContactInfo } from "../../../../../../../Utils/Helpers/common";
import { useParams } from "react-router";
import { toastSeverity } from "../../../../../../../Utils/config/toastConfig";
import {
  submitPaymentDetails,
  lookupBankBySortCode,
} from "./AddPaymentDetails.helper";
const SORT_CODE_REGEX = /^\d{2}-\d{2}-\d{2}$/;
const SORT_CODE_FORMAT_ERROR =
  "Please enter the Sort Code in the format 12-34-56";

const AddPaymentDetails = forwardRef((props, ref) => {
  const classes = useStyles();
  const { afterClose, setToast, isEditMode, recordId } = props;
  const apiUrl = process.env.BANK_FINDER_API_URL;
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [enumoptions, setEnumOptions] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSortCodeLookupLoading, setIsSortCodeLookupLoading] = useState(false);
  const [bankCountryInputMode, setBankCountryInputMode] = useState("manual");
  const [isBankDetailsReadOnly, setIsBankDetailsReadOnly] = useState(false);
  const lastLookedUpSortCodeRef = useRef("");
  const user = useSelector((state) => state.oidc.user);
  const { crmContactUserEmail } = getCRMContactInfo();
  let logedInUserData = {
    createdBy: crmContactUserEmail || user?.profile?.email || "",
    createdDate: Math.floor(Date.now() / 1000),
  };

  let { crmId, pageUrl } = useParams();
  useEffect(() => {
    getEnumOptionsForField();
  }, []);

  const getEnumOptionsForField = async () => {
    const opionsetCategories = "Country,BankVerificationStatus,BankType";
    try {
      setIsLoading(true);
      const payload = [
        {
          id: "Borrower_Dropdowns",
          spName: "[DF].[usp_GetOptionSetsByCategories]",
          parameters: {
            Categories: opionsetCategories,
          },
        },
      ];

      if (isEditMode) {
        payload.push({
          id: "Prefilled_Payment_Details",
          spName: "DF.usp_PrefilledPaymentDetails",
          parameters: {
            id: recordId,
          },
        });
      }

      const response = await ExecuteSqlStoreProc(payload);

      // Handle Dropdowns
      const dropdownData = response?.Borrower_Dropdowns || [];
      const categories = opionsetCategories.split(",").map((c) => c.trim());
      const optionSetObject = buildBorrowerOptionSetObject(
        categories,
        dropdownData
      );

      if (Object.keys(optionSetObject).length !== 0) {
        setEnumOptions(optionSetObject);
        setApiError("");

        // Pre-select "Unverified" for Bank Verification Status if not in edit mode
        if (!isEditMode && optionSetObject["BankVerificationStatus"]) {
          const unverifiedOpt = optionSetObject["BankVerificationStatus"].find(
            (opt) =>
              opt?.label?.toString().toLowerCase().includes("unverified") ||
              opt?.value?.toString().toLowerCase().includes("unverified")
          );
          if (unverifiedOpt) {
            setFormData((prev) => ({
              ...prev,
              BorrowerBankVerificationStatus: unverifiedOpt.value,
            }));
          }
        }
      } else {
        setEnumOptions([]);
      }

      // Handle Prefilled Data
      const prefilledData = response?.Prefilled_Payment_Details?.[0];
      if (prefilledData) {
        setFormData((prev) => ({
          ...prev,
          ...prefilledData,
        }));

        const bankFoundValue = prefilledData.found;
        const isBankFound =
          bankFoundValue === true ||
          bankFoundValue === "true" ||
          bankFoundValue === 1 ||
          bankFoundValue === "1";
        setIsBankDetailsReadOnly(isBankFound);
        setBankCountryInputMode(isBankFound ? "api" : "manual");
        if (prefilledData.BorrowerSortCode) {
          lastLookedUpSortCodeRef.current = prefilledData.BorrowerSortCode;
        }
      }
    } catch (error) {
      setEnumOptions([]);
      setApiError("Failed to load form options. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const getEnumOptions = (field) => {
    const optionsFromEnum = enumoptions[field];
    if (
      !optionsFromEnum ||
      (Array.isArray(optionsFromEnum) && optionsFromEnum.length === 0)
    ) {
      return [];
    }
    if (Array.isArray(optionsFromEnum)) {
      return optionsFromEnum.map((data) => ({
        id: data?.id,
        label: toOptionLabel(data?.label),
        value: data?.value,
      }));
    }
    return [];
  };

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => {
      return Object.keys(formData).length > 0;
    },
  }));

  const handleInputChange = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    if (errors[fieldId]) {
      setErrors((prev) => ({
        ...prev,
        [fieldId]: "",
      }));
    }
    if (apiError) {
      setApiError("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const requiredFields = [
      "BorrowerBeneficiaryName",
      "BorrowerAccountNumber",
      "BorrowerSortCode",
      "BorrowerBankType",
      "BorrowerBankName",
      "BorrowerBankTown",
      "BorrowerBankPostcode",
      "BorrowerBankCountry",
      "BorrowerBankAddress",
      "BorrowerAddress",
      "BorrowerTown",
      "BorrowerPostcode",
      "BorrowerPaymentCountry",
    ];
    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
      }
    });

    if (
      formData.BorrowerSortCode &&
      !SORT_CODE_REGEX.test(formData.BorrowerSortCode)
    ) {
      newErrors.BorrowerSortCode = SORT_CODE_FORMAT_ERROR;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      let payload = {
        ...formData,
        loanId: crmId,
        pageUrl: pageUrl,
        ...logedInUserData,
        BorrowerBeneficiaryType: "Borrower",
      };

      if (isEditMode) {
        payload.id = recordId;
      }

      delete payload.HasThirdParty;
      delete payload.ThirdPartyCount;
      for (let i = 0; i < 5; i++) {
        delete payload[`ThirdPartyType_${i}`];
        delete payload[`ThirdPartyName_${i}`];
      }

      const response = await submitPaymentDetails(isEditMode, payload);
      if (response?.response?.data?.Message) {
        setApiError(
          response?.response?.data?.Message || "Something went wrong"
        );
      } else {
        setToast({
          type: toastSeverity.SUCCESS,
          severity: toastSeverity.SUCCESS,
          message: "Payment Details Added Successfully",
          status: true,
        });
        afterClose({ pageUrl, crmId });
      }
    } catch (error) {
      setApiError(error?.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectChange = (selectedOption, id) => {
    handleInputChange(id, selectedOption?.value || "");
  };

  const handleTextChange = (event, fieldName) => {
    handleInputChange(fieldName, event.target.value);
  };

  const handleAccountNumberChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");
    handleInputChange("BorrowerAccountNumber", value);
  };

  const handleSortCodeChange = async (event) => {
    const value = event.target.value;
    handleInputChange("BorrowerSortCode", value);

    if (!value) {
      lastLookedUpSortCodeRef.current = "";
      return;
    }

    if (!SORT_CODE_REGEX.test(value)) {
      if (value.length >= 8) {
        setErrors((prev) => ({
          ...prev,
          BorrowerSortCode: SORT_CODE_FORMAT_ERROR,
        }));
      }
      return;
    }

    if (lastLookedUpSortCodeRef.current === value) {
      return;
    }
    lastLookedUpSortCodeRef.current = value;

    if (!apiUrl) {
      if (isBankDetailsReadOnly) {
        setFormData((prev) => ({
          ...prev,
          BorrowerBankName: "",
          BorrowerBankTown: "",
          BorrowerBankPostcode: "",
          BorrowerBankCountry: "",
        }));
      }
      handleInputChange("found", false);
      setBankCountryInputMode("manual");
      setIsBankDetailsReadOnly(false);
      return;
    }

    setIsSortCodeLookupLoading(true);
    try {
      const result = await lookupBankBySortCode(
        value.replace(/-/g, ""),
        apiUrl
      );
      if (result?.found) {
        const hasCountry = Boolean(result.country);
        setFormData((prev) => ({
          ...prev,
          BorrowerBankName: result.bankName || prev.BorrowerBankName,
          BorrowerBankTown: result.town || prev.BorrowerBankTown,
          BorrowerBankPostcode: result.postcode || prev.BorrowerBankPostcode,
          BorrowerBankCountry: hasCountry
            ? result.country
            : prev.BorrowerBankCountry,
          found: true,
        }));
        setBankCountryInputMode(hasCountry ? "api" : "manual");
        setIsBankDetailsReadOnly(true);
        setApiError("");
      } else {
        if (isBankDetailsReadOnly) {
          // Previous bank details came from a different sort code's lookup -
          // they no longer apply, so clear them rather than leaving stale data.
          setFormData((prev) => ({
            ...prev,
            BorrowerBankName: "",
            BorrowerBankTown: "",
            BorrowerBankPostcode: "",
            BorrowerBankCountry: "",
          }));
        }
        handleInputChange("found", false);
        setBankCountryInputMode("manual");
        setIsBankDetailsReadOnly(false);
        setApiError(result?.error || "No bank found for this sort code");
      }
    } catch (error) {
      if (isBankDetailsReadOnly) {
        setFormData((prev) => ({
          ...prev,
          BorrowerBankName: "",
          BorrowerBankTown: "",
          BorrowerBankPostcode: "",
          BorrowerBankCountry: "",
        }));
      }
      handleInputChange("found", false);
      setBankCountryInputMode("manual");
      setIsBankDetailsReadOnly(false);
      setApiError("Failed to look up bank details. Please try again.");
    } finally {
      setIsSortCodeLookupLoading(false);
    }
  };

  const renderBorrowerDetailsFields = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerBeneficiaryName"
          name="BorrowerBeneficiaryName"
          label="Beneficiary Name"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerBeneficiaryName}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerBeneficiaryName")}
          required={true}
          error={Boolean(errors.BorrowerBeneficiaryName)}
          helperText={errors.BorrowerBeneficiaryName}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_Select
          id="BorrowerBankType"
          name="BorrowerBankType"
          label="Bank Type"
          fullWidth
          variant="standard"
          required={true}
          defaultValue={formData.BorrowerBankType || ""}
          options={getEnumOptions("BankType")}
          onChange={(selectedOption) =>
            handleSelectChange(selectedOption, "BorrowerBankType")
          }
          error={Boolean(errors.BorrowerBankType)}
          helperText={errors.BorrowerBankType}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerAccountNumber"
          name="BorrowerAccountNumber"
          label="Account Number"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerAccountNumber}
          inputType="value"
          inputMaxLength={10}
          onChange={handleAccountNumberChange}
          required={true}
          error={Boolean(errors.BorrowerAccountNumber)}
          helperText={errors.BorrowerAccountNumber}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerSortCode"
          name="BorrowerSortCode"
          label="Sort Code"
          placeholder="12-34-56"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerSortCode}
          inputType="value"
          onChange={handleSortCodeChange}
          required={true}
          error={Boolean(errors.BorrowerSortCode)}
          helperText={
            isSortCodeLookupLoading
              ? "Fetching bank details, please wait..."
              : errors.BorrowerSortCode
          }
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerBankName"
          name="BorrowerBankName"
          label="Creditor Bank Name"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerBankName}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerBankName")}
          required={true}
          disabled={isSortCodeLookupLoading || isBankDetailsReadOnly}
          error={Boolean(errors.BorrowerBankName)}
          helperText={errors.BorrowerBankName}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerBankTown"
          name="BorrowerBankTown"
          label="Creditor Bank Town"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerBankTown}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerBankTown")}
          required={true}
          disabled={isSortCodeLookupLoading || isBankDetailsReadOnly}
          error={Boolean(errors.BorrowerBankTown)}
          helperText={errors.BorrowerBankTown}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerBankPostcode"
          name="BorrowerBankPostcode"
          label="Creditor Bank Postcode"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerBankPostcode}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerBankPostcode")}
          required={true}
          disabled={isSortCodeLookupLoading || isBankDetailsReadOnly}
          error={Boolean(errors.BorrowerBankPostcode)}
          helperText={errors.BorrowerBankPostcode}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        {bankCountryInputMode === "api" ? (
          <A_TextField
            id="BorrowerBankCountry"
            name="BorrowerBankCountry"
            label="Creditor Bank Country"
            variant="standard"
            fullWidth
            defaultValue={formData.BorrowerBankCountry}
            inputType="value"
            onChange={(e) => handleTextChange(e, "BorrowerBankCountry")}
            required={true}
            disabled={isSortCodeLookupLoading || isBankDetailsReadOnly}
            error={Boolean(errors.BorrowerBankCountry)}
            helperText={errors.BorrowerBankCountry}
          />
        ) : (
          <A_Select
            id="BorrowerBankCountry"
            label="Creditor Bank Country"
            fullWidth
            variant="standard"
            required={true}
            isCascading={true}
            defaultValue={formData.BorrowerBankCountry || ""}
            options={getEnumOptions("Country")}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, "BorrowerBankCountry")
            }
            disabled={isSortCodeLookupLoading || isBankDetailsReadOnly}
            error={Boolean(errors.BorrowerBankCountry)}
            helperText={errors.BorrowerBankCountry}
          />
        )}
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerBankAddress"
          name="BorrowerBankAddress"
          label="Creditor Bank Address"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerBankAddress}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerBankAddress")}
          required={true}
          error={Boolean(errors.BorrowerBankAddress)}
          helperText={errors.BorrowerBankAddress}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerAddress"
          name="BorrowerAddress"
          label="Creditor Address"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerAddress}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerAddress")}
          required={true}
          error={Boolean(errors.BorrowerAddress)}
          helperText={errors.BorrowerAddress}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerTown"
          name="BorrowerTown"
          label="Creditor Town"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerTown}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerTown")}
          required={true}
          error={Boolean(errors.BorrowerTown)}
          helperText={errors.BorrowerTown}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_TextField
          id="BorrowerPostcode"
          name="BorrowerPostcode"
          label="Creditor Postcode"
          variant="standard"
          fullWidth
          defaultValue={formData.BorrowerPostcode}
          inputType="value"
          onChange={(e) => handleTextChange(e, "BorrowerPostcode")}
          required={true}
          error={Boolean(errors.BorrowerPostcode)}
          helperText={errors.BorrowerPostcode}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_Select
          id="BorrowerPaymentCountry"
          label="Creditor Country"
          fullWidth
          variant="standard"
          required={true}
          defaultValue={formData.BorrowerPaymentCountry || ""}
          options={getEnumOptions("Country")}
          onChange={(selectedOption) =>
            handleSelectChange(selectedOption, "BorrowerPaymentCountry")
          }
          error={Boolean(errors.BorrowerPaymentCountry)}
          helperText={errors.BorrowerPaymentCountry}
          // disabled={isPrefilled}
        />
      </Grid>
      <Grid item xs={12} className={classes.fieldGrid}>
        <A_Select
          id="BorrowerBankVerificationStatus"
          label="Bank Verification Status"
          fullWidth
          variant="standard"
          defaultValue={formData.BorrowerBankVerificationStatus || ""}
          options={getEnumOptions("BankVerificationStatus")}
          onChange={(selectedOption) =>
            handleSelectChange(selectedOption, "BorrowerBankVerificationStatus")
          }
          error={Boolean(errors.BorrowerBankVerificationStatus)}
          helperText={errors.BorrowerBankVerificationStatus}
          disabled={!isEditMode}
        />
      </Grid>
    </Grid>
  );

  return (
    <div className={classes.root}>
      <Paper elevation={0} className={classes.formCard}>
        {apiError && (
          <Box
            mb={2}
            style={{
              backgroundColor: "#fff3cd",
              padding: "10px",
              borderRadius: "calc(4px * var(--app-radius-scale, 1))",
            }}
          >
            <Typography color="error" variant="body2">
              {apiError}
            </Typography>
          </Box>
        )}

        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        ) : (
          <div className={classes.fieldsContainer}>
            {renderBorrowerDetailsFields()}
          </div>
        )}

        <div className={classes.actions}>
          <div className={classes.actionGroup} />
          <A_Button
            label="Finish"
            onClick={handleSubmit}
            premiumVariant="primary"
            disabled={isSubmitting}
            className={classes.actionButton}
          />
        </div>
      </Paper>
    </div>
  );
});

AddPaymentDetails.displayName = "AddPaymentDetails";

AddPaymentDetails.propTypes = {
  onClose: PropTypes.func,
  afterClose: PropTypes.any,
  setToast: PropTypes.func,
  isEditMode: PropTypes.bool,
  recordId: PropTypes.string,
};

const mapStateToProps = (state) => ({
  setToast: state.toast.setToast,
});

const mapDispatchToProps = (dispatch) => ({
  setToast: bindActionCreators(toastActions.setToast, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddPaymentDetails);
